import {
  useEffect,
  useEffectEvent,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type PointerEvent,
} from "react";
import type { Authorization, FloorItem, FloorRoom } from "../../api/types";
import { useTill } from "../../app/till-context";
import { TableCardsDialog, type TableCard } from "../table-order/TableCardsDialog";
import { t } from "../../i18n";
import { ConfirmDialog } from "../../ui/ConfirmDialog";
import {
  freeSpot,
  HEIGHT,
  hits,
  minThin,
  moveTo,
  MIN_SIZE,
  newId,
  newItem,
  newRoomName,
  resizeTo,
  snapTo,
  tableLabels,
  WIDTH,
  withItem,
  withRoom,
} from "./floor-edit";

interface Props {
  /** The owner's Beheer code. */
  authorization: Authorization;
}

const position = (item: { x: number; y: number; w: number; h: number }): CSSProperties => ({
  left: `${String((item.x / WIDTH) * 100)}%`,
  top: `${String((item.y / HEIGHT) * 100)}%`,
  width: `${String((item.w / WIDTH) * 100)}%`,
  height: `${String((item.h / HEIGHT) * 100)}%`,
});

/** A field that saves when it is left or with Enter (the prototype's onchange). */
function SaveField({
  value,
  label,
  maxLength,
  numeric,
  onSave,
}: {
  value: string;
  label: string;
  maxLength?: number;
  numeric?: boolean;
  onSave: (text: string) => void;
}) {
  const [text, setText] = useState<string | null>(null);
  const done = () => {
    if (text !== null && text !== value) onSave(text);
    setText(null);
  };
  return (
    <label>
      {label}
      <input
        value={text ?? value}
        maxLength={maxLength}
        inputMode={numeric ? "numeric" : undefined}
        className={numeric ? "num" : undefined}
        onChange={(event) => {
          setText(event.target.value);
        }}
        onBlur={done}
        onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
          if (event.key === "Enter") event.currentTarget.blur();
        }}
      />
    </label>
  );
}

interface Drag {
  id: string;
  resize: boolean;
  startX: number;
  startY: number;
  box: { x: number; y: number; w: number; h: number };
  moved: boolean;
}

/**
 * Beheer › Tafelplan, as in the prototype: per room a plan of 1000 × 640 where tables, bar, walls and
 * text are dragged, resized with the corner and changed in the side panel. Items never overlap; an
 * occupied table cannot be renamed or removed. Every change is saved at once and the till's
 * "Kies tafel" shows it.
 */
export function FloorScreen({ authorization }: Props) {
  const { api, floor, floorChanged, openTables, report, toast, status, general } = useTill();
  /**
   * QR-bestellen aan tafel: the owner switches it on or off, and "Eerst goedkeuren aan de kassa"; then a card per
   * table to print. Null while asked.
   */
  const [tableQr, setTableQr] = useState<{ on: boolean; approve?: boolean; cards: TableCard[] } | null>(null);
  const [showCards, setShowCards] = useState(false);
  const [switching, setSwitching] = useState(false);
  const switchTableQr = async (change: { on?: boolean; approve?: boolean }) => {
    setSwitching(true);
    try {
      const answer = await api.request<{ on: boolean; approve: boolean; cards: TableCard[] }>(
        "PUT",
        "/device/table-qr",
        {
          staffToken: authorization.token,
          body: change,
        },
      );
      setTableQr(answer);
      toast(
        change.approve === undefined
          ? t.floorAdmin.qrSwitched(answer.on)
          : t.floorAdmin.qrApproveSwitched(answer.approve),
      );
    } catch (error) {
      report(error);
    } finally {
      setSwitching(false);
    }
  };
  const [rooms, setRooms] = useState<FloorRoom[]>(floor);
  const [roomId, setRoomId] = useState(floor[0]?.id ?? "");
  const [selected, setSelected] = useState<string | null>(null);
  const [snap, setSnap] = useState(true);
  /**
   * Locked, nothing moves or changes size by accident: on a touch screen the plan opens locked, as a
   * finger that only meant to scroll the page took a table along (Furkan, 10/10/2026). Tapping a table to
   * change it in the side panel still works. With a mouse it opens unlocked, as before.
   */
  const [locked, setLocked] = useState(
    () => typeof window.matchMedia === "function" && window.matchMedia("(pointer: coarse)").matches,
  );
  const [saved, setSaved] = useState(false);
  const [message, setMessage] = useState("");
  const [hit, setHit] = useState(false);
  const [confirm, setConfirm] = useState<"room" | "reset" | null>(null);
  const drag = useRef<Drag | null>(null);
  const canvas = useRef<HTMLDivElement>(null);
  /** Saves in order; only the answer to the latest one replaces what is on screen. */
  const saving = useRef({ last: 0, chain: Promise.resolve() });
  const lastSaved = useRef(floor);
  const text = t.floorAdmin;

  // the cards follow the plan: asked again when a table was added or renamed
  useEffect(() => {
    let stopped = false;
    api
      .request<{ on: boolean; cards: TableCard[] }>("GET", "/device/table-qr", { retry: true })
      .then((answer) => {
        if (!stopped) setTableQr(answer);
      })
      .catch(() => {
        // not asked yet on an older server, or offline: the switch stays as "uit"
      });
    return () => {
      stopped = true;
    };
  }, [api, floor]);

  // a change made on another device comes in live: take it over when nothing is being saved here
  useEffect(() => {
    if (saving.current.last === 0 || floor !== lastSaved.current) {
      lastSaved.current = floor;
      setRooms(floor);
    }
  }, [floor]);

  const room = rooms.find((item) => item.id === roomId) ?? rooms[0];
  const item = room?.items.find((candidate) => candidate.id === selected) ?? null;
  const busy = (candidate: FloorItem | null) =>
    candidate?.kind === "table" && candidate.label !== null && openTables.has(candidate.label);

  const commit = (next: FloorRoom[]) => {
    setRooms(next);
    const number = ++saving.current.last;
    saving.current.chain = saving.current.chain.then(async () => {
      try {
        const answer = await api.request<{ rooms: FloorRoom[] }>("PUT", "/device/floor", {
          staffToken: authorization.token,
          body: { rooms: next.map(({ id, name, items }) => ({ id, name, items })) },
        });
        lastSaved.current = answer.rooms;
        floorChanged(answer.rooms);
        setSaved(true);
        if (number === saving.current.last) setRooms(answer.rooms);
      } catch (error) {
        report(error);
        if (number === saving.current.last) setRooms(lastSaved.current);
      }
    });
  };

  const changeItem = (changed: FloorItem | null, id = item?.id) => {
    if (!room || id === undefined) return;
    commit(withRoom(rooms, withItem(room, id, changed)));
  };

  const add = (kind: "rect" | "round" | "bar" | "wall" | "text") => {
    if (!room) return;
    const created = newItem(rooms, room, kind);
    commit(withRoom(rooms, { ...room, items: [...room.items, created] }));
    setSelected(created.id);
    setMessage("");
  };

  const remove = () => {
    if (!item || busy(item)) return;
    changeItem(null);
    setSelected(null);
  };

  // arrow keys move the selected item (Shift: by 1), Delete removes it (prototype)
  const onKey = useEffectEvent((event: globalThis.KeyboardEvent) => {
    const target = event.target as HTMLElement | null;
    if (!item || !room || (target && /INPUT|SELECT|TEXTAREA/.test(target.tagName))) return;
    if (document.querySelector(".scrim")) return;
    const step = event.shiftKey ? 1 : 10;
    const delta = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] }[
      event.key
    ];
    if (delta) {
      event.preventDefault();
      if (locked) {
        toast(text.lock.refused);
        return;
      }
      const box = {
        x: Math.max(0, Math.min(WIDTH - item.w, item.x + delta[0]!)),
        y: Math.max(0, Math.min(HEIGHT - item.h, item.y + delta[1]!)),
        w: item.w,
        h: item.h,
      };
      if (hits(room, item, box)) {
        toast(text.blocked);
        return;
      }
      changeItem({ ...item, ...box });
    } else if (event.key === "Delete" || event.key === "Backspace") {
      event.preventDefault();
      remove();
    }
  });
  useEffect(() => {
    const listener = (event: globalThis.KeyboardEvent) => {
      onKey(event);
    };
    window.addEventListener("keydown", listener);
    return () => {
      window.removeEventListener("keydown", listener);
    };
  }, []);

  if (!room) return <div className="panel admin-section" aria-busy="true" />;

  const scale = () => {
    const box = canvas.current?.getBoundingClientRect();
    return box ? { x: WIDTH / box.width, y: HEIGHT / box.height } : { x: 1, y: 1 };
  };

  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement;
    const handle = target.closest<HTMLElement>("[data-handle]");
    const element = handle ?? target.closest<HTMLElement>("[data-id]");
    if (!element) {
      setSelected(null);
      setMessage("");
      return;
    }
    const id = element.dataset.handle ?? element.dataset.id ?? "";
    const picked = room.items.find((candidate) => candidate.id === id);
    if (!picked) return;
    if (picked.id !== selected) setMessage("");
    setSelected(picked.id);
    // locked: a tap only chooses the item; the finger may go on scrolling the page
    if (locked) return;
    event.preventDefault();
    drag.current = {
      id: picked.id,
      resize: handle !== null,
      startX: event.clientX,
      startY: event.clientY,
      box: { x: picked.x, y: picked.y, w: picked.w, h: picked.h },
      moved: false,
    };
    canvas.current?.setPointerCapture(event.pointerId);
  };

  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const current = drag.current;
    if (!current) return;
    const factor = scale();
    const dx = (event.clientX - current.startX) * factor.x;
    const dy = (event.clientY - current.startY) * factor.y;
    if (Math.abs(dx) + Math.abs(dy) > 2) current.moved = true;
    const moving = room.items.find((candidate) => candidate.id === current.id);
    if (!moving) return;
    const result = current.resize
      ? resizeTo(room, moving, snapTo(current.box.w + dx, snap), snapTo(current.box.h + dy, snap))
      : moveTo(room, moving, snapTo(current.box.x + dx, snap), snapTo(current.box.y + dy, snap));
    setHit(result.hit);
    setRooms((all) => withRoom(all, withItem(room, moving.id, { ...moving, ...result.box })));
  };

  const onPointerUp = () => {
    const current = drag.current;
    drag.current = null;
    setHit(false);
    if (current?.moved) commit(rooms);
  };

  const labels = tableLabels(rooms).map((label) => label.toUpperCase());
  const roomTables = room.items.filter((candidate) => candidate.kind === "table");
  const roomBusy = roomTables.some((candidate) => busy(candidate));

  const rename = (value: string) => {
    if (!item) return;
    const label = value.trim().toUpperCase();
    const problem = !label
      ? text.noLabel
      : label !== item.label && labels.includes(label)
        ? text.exists(label)
        : busy(item)
          ? text.busyRename
          : "";
    setMessage(problem);
    if (problem === "") changeItem({ ...item, label });
  };

  const setSize = (which: "w" | "h", value: string) => {
    if (!item) return;
    const number = Number.parseInt(value, 10);
    if (!Number.isFinite(number)) return;
    let w = which === "w" ? number : item.w;
    let h = which === "h" ? number : item.h;
    if (item.shape === "round") w = h = which === "w" ? w : h;
    w = Math.max(MIN_SIZE, Math.min(WIDTH - item.x, w));
    h = Math.max(minThin(item), Math.min(HEIGHT - item.y, h));
    if (hits(room, item, { x: item.x, y: item.y, w, h })) {
      toast(text.tooBig);
      return;
    }
    changeItem({ ...item, w, h });
  };

  const setShape = (shape: "rect" | "round") => {
    if (!item) return;
    if (shape === "rect") {
      changeItem({ ...item, shape });
      return;
    }
    let size = Math.min(item.w, HEIGHT - item.y);
    if (hits(room, item, { x: item.x, y: item.y, w: size, h: size })) size = Math.min(item.w, item.h);
    changeItem({ ...item, shape, w: size, h: size });
  };

  const duplicate = () => {
    if (item?.kind !== "table") return;
    // the same table with the next free number, on the first free spot (prototype)
    const copy = {
      ...newItem(rooms, room, item.shape),
      w: item.w,
      h: item.h,
      seats: item.seats,
      ...freeSpot(room, item.w, item.h),
    };
    commit(withRoom(rooms, { ...room, items: [...room.items, copy] }));
    setSelected(copy.id);
  };

  const addRoom = () => {
    const created: FloorRoom = { id: newId(), name: newRoomName(rooms), position: rooms.length, items: [] };
    commit([...rooms, created]);
    setRoomId(created.id);
    setSelected(null);
  };

  const renameRoom = (value: string) => {
    const name = value.trim();
    if (!name || name === room.name) return;
    if (rooms.some((other) => other.id !== room.id && other.name.toLowerCase() === name.toLowerCase())) {
      toast(text.roomExists, "bad");
      return;
    }
    commit(withRoom(rooms, { ...room, name }));
  };

  const removeRoom = () => {
    setConfirm(null);
    const left = rooms.filter((other) => other.id !== room.id);
    commit(left);
    setRoomId(left[0]?.id ?? "");
    setSelected(null);
  };

  const reset = async () => {
    setConfirm(null);
    try {
      const answer = await api.request<{ rooms: FloorRoom[] }>("POST", "/device/floor/reset", {
        staffToken: authorization.token,
      });
      lastSaved.current = answer.rooms;
      floorChanged(answer.rooms);
      setRooms(answer.rooms);
      setRoomId(answer.rooms[0]?.id ?? "");
      setSelected(null);
      setSaved(true);
    } catch (error) {
      report(error);
    }
  };

  const roomBlock = (
    <div className="grp">
      <h3>{text.room}</h3>
      <SaveField key={`room-${room.id}`} value={room.name} label={text.roomName} onSave={renameRoom} />
      <div className="note fpe-count">
        {text.roomCount(
          roomTables.length,
          roomTables.reduce((sum, table) => sum + (table.seats ?? 0), 0),
        )}
      </div>
      <button
        type="button"
        className="btn danger"
        disabled={rooms.length < 2 || roomBusy}
        onClick={() => {
          setConfirm("room");
        }}
      >
        {text.removeRoom}
      </button>
      {roomBusy && <p className="note">{text.busyRoom}</p>}
      <button
        type="button"
        className="linkbtn note fpe-reset"
        onClick={() => {
          if (openTables.size > 0) toast(text.payFirst, "bad");
          else setConfirm("reset");
        }}
      >
        {text.reset}
      </button>
    </div>
  );

  return (
    <div className="panel admin-section floor-admin">
      <h2>{text.title}</h2>
      <div className="qrmod">
        <div>
          <b>{text.qrTitle}</b>{" "}
          <span className={`pill${tableQr?.on ? " ok" : ""}`}>{tableQr?.on ? text.qrOnNow : text.qrOff}</span>
          <p className="note">{tableQr?.on ? text.qrHint : text.qrLater}</p>
          {tableQr?.on && (
            <label className="inl qrmod-approve">
              <input
                type="checkbox"
                checked={tableQr.approve === true}
                disabled={switching}
                onChange={(event) => void switchTableQr({ approve: event.target.checked })}
              />{" "}
              {text.qrApprove}
            </label>
          )}
        </div>
        <div className="qrmod-b">
          {tableQr?.on && (
            <button
              type="button"
              className="btn primary"
              onClick={() => {
                setShowCards(true);
              }}
            >
              {t.tableCards.button}
            </button>
          )}
          <button
            type="button"
            className={tableQr?.on ? "btn" : "btn primary"}
            disabled={tableQr === null || switching}
            onClick={() => {
              void switchTableQr({ on: !tableQr?.on });
            }}
          >
            {tableQr?.on ? text.qrOffButton : text.qrOn}
          </button>
        </div>
      </div>
      {showCards && tableQr && (
        <TableCardsDialog
          shop={[general?.displayName, general?.receiptName].find((name) => name) ?? status.shop.name}
          cards={tableQr.cards}
          onClose={() => {
            setShowCards(false);
          }}
        />
      )}
      <div className="fpe-top">
        <div className="seg fp-rooms" role="tablist" aria-label={text.title}>
          {rooms.map((other) => (
            <button
              key={other.id}
              type="button"
              role="tab"
              aria-selected={other.id === room.id}
              aria-pressed={other.id === room.id}
              onClick={() => {
                setRoomId(other.id);
                setSelected(null);
                setMessage("");
              }}
            >
              {other.name}{" "}
              <span className="note">{other.items.filter((candidate) => candidate.kind === "table").length}</span>
            </button>
          ))}
        </div>
        <button type="button" className="btn" onClick={addRoom}>
          {text.addRoom}
        </button>
        <span className="spacer" />
        <span className="note" role="status">
          {saved ? text.saved : ""}
        </span>
      </div>
      <div className="fpe">
        <div>
          <div className="fpe-bar">
            {(["rect", "round", "bar", "wall", "text"] as const).map((kind) => (
              <button
                key={kind}
                type="button"
                className="btn"
                onClick={() => {
                  add(kind);
                }}
              >
                {text.add[kind]}
              </button>
            ))}
            <button
              type="button"
              className="btn fpe-lock"
              aria-pressed={locked}
              title={text.lock.hint}
              onClick={() => {
                setLocked(!locked);
              }}
            >
              {locked ? text.lock.on : text.lock.off}
            </button>
            <label className="inl fpe-snap">
              <input
                type="checkbox"
                checked={snap}
                onChange={(event) => {
                  setSnap(event.target.checked);
                }}
              />{" "}
              {text.snap}
            </label>
          </div>
          <div
            ref={canvas}
            className={`fp edit${snap ? " grid" : ""}${locked ? " locked" : ""}`}
            role="group"
            aria-label={room.name}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
          >
            {room.items.map((candidate) => {
              const on = candidate.id === selected;
              const classes = `fpi${on ? " on" : ""}${on && hit ? " hit" : ""}`;
              if (candidate.kind === "table") {
                return (
                  <div
                    key={candidate.id}
                    data-id={candidate.id}
                    className={`fpt ${candidate.shape} ${classes}${busy(candidate) ? " busy" : ""}`}
                    style={position(candidate)}
                    aria-label={text.table(candidate.label ?? "")}
                  >
                    <b>{candidate.label}</b>
                    <small>{text.seatsShort(candidate.seats ?? 0)}</small>
                    <i className="fph" data-handle={candidate.id} />
                  </div>
                );
              }
              return (
                <div
                  key={candidate.id}
                  data-id={candidate.id}
                  className={`fpd ${candidate.kind} ${classes}`}
                  style={position(candidate)}
                  aria-label={text.decor[candidate.kind]}
                >
                  {candidate.text}
                  <i className="fph" data-handle={candidate.id} />
                </div>
              );
            })}
          </div>
          <p className="note">{text.hint}</p>
        </div>
        <aside
          className="fpe-side"
          aria-label={item ? (item.kind === "table" ? text.table(item.label ?? "") : text.decor[item.kind]) : text.room}
        >
          {!item && <p className="note">{text.pick}</p>}
          {item?.kind === "table" && (
            <div className="grp">
              <h3>{text.table(item.label ?? "")}</h3>
              <SaveField
                key={`label-${item.id}-${item.label ?? ""}`}
                value={item.label ?? ""}
                label={text.label}
                maxLength={6}
                onSave={rename}
              />
              <p className="pinmsg left" role="alert">
                {message}
              </p>
              <span className="fpe-l">{text.shape}</span>
              <div className="seg" role="group" aria-label={text.shape}>
                {(["rect", "round"] as const).map((shape) => (
                  <button
                    key={shape}
                    type="button"
                    aria-pressed={item.shape === shape}
                    onClick={() => {
                      setShape(shape);
                    }}
                  >
                    {text[shape]}
                  </button>
                ))}
              </div>
              <span className="fpe-l">{text.seats}</span>
              <div className="qty">
                <button
                  type="button"
                  aria-label={text.fewer}
                  onClick={() => {
                    changeItem({ ...item, seats: Math.max(1, (item.seats ?? 1) - 1) });
                  }}
                >
                  −
                </button>
                <output>{item.seats}</output>
                <button
                  type="button"
                  aria-label={text.more}
                  onClick={() => {
                    changeItem({ ...item, seats: Math.min(30, (item.seats ?? 1) + 1) });
                  }}
                >
                  +
                </button>
              </div>
              <div className="fpe-wh">
                <SaveField
                  key={`w-${item.id}-${String(item.w)}`}
                  value={String(item.w)}
                  label={text.width}
                  numeric
                  onSave={(value) => setSize("w", value)}
                />
                <SaveField
                  key={`h-${item.id}-${String(item.h)}`}
                  value={String(item.h)}
                  label={text.height}
                  numeric
                  onSave={(value) => setSize("h", value)}
                />
              </div>
              <div className="fpe-row">
                <button type="button" className="btn" onClick={duplicate}>
                  {text.duplicate}
                </button>
                <button type="button" className="btn danger" disabled={busy(item)} onClick={remove}>
                  {text.remove}
                </button>
              </div>
              {busy(item) && <p className="note">{text.busyRemove(item.label ?? "")}</p>}
            </div>
          )}
          {item && item.kind !== "table" && (
            <div className="grp">
              <h3>{text.decor[item.kind]}</h3>
              <SaveField
                key={`text-${item.id}`}
                value={item.text ?? ""}
                label={text.text}
                maxLength={60}
                onSave={(value) => changeItem({ ...item, text: value })}
              />
              <div className="fpe-wh">
                <SaveField
                  key={`w-${item.id}-${String(item.w)}`}
                  value={String(item.w)}
                  label={text.width}
                  numeric
                  onSave={(value) => setSize("w", value)}
                />
                <SaveField
                  key={`h-${item.id}-${String(item.h)}`}
                  value={String(item.h)}
                  label={text.height}
                  numeric
                  onSave={(value) => setSize("h", value)}
                />
              </div>
              <div className="fpe-row">
                <button type="button" className="btn danger" onClick={remove}>
                  {text.remove}
                </button>
              </div>
            </div>
          )}
          {roomBlock}
        </aside>
      </div>
      {confirm === "room" && (
        <ConfirmDialog
          message={text.removeRoomConfirm(room.name, roomTables.length)}
          confirm={text.remove}
          onConfirm={removeRoom}
          onClose={() => {
            setConfirm(null);
          }}
        />
      )}
      {confirm === "reset" && (
        <ConfirmDialog
          message={text.resetConfirm}
          confirm={text.resetButton}
          onConfirm={() => void reset()}
          onClose={() => {
            setConfirm(null);
          }}
        />
      )}
    </div>
  );
}
